/**
 * Ticket KL4: Kontakte aus einer Datei einlesen – vier Schritte in einer
 * Ansicht (Datei, Zuordnung, Werteübersetzung und Vorschau, Übernehmen).
 *
 * Die Datei wird ausschließlich im Browser gelesen und nie hochgeladen. Der
 * Schreibvorgang läuft in einer Datenbankfunktion, also in einer Transaktion:
 * ein Abbruch hinterlässt keinen halben Bestand. Ausschließlich für
 * Administratoren; die Serverfunktionen prüfen die Rolle erneut.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { useAdminSession } from "@/hooks/use-admin-session";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminTableShell } from "@/components/admin/AdminTableShell";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminField, adminInputClass } from "@/components/admin/AdminField";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";
import { Button } from "@/components/ui/button";
import { listContactSources } from "@/lib/contact-sources.functions";
import { selectableSources } from "@/lib/contact-sources";
import {
  BUNDESLAENDER,
  BUNDESLAND_LABELS,
  CONTACT_LEGAL_BASES,
  CONTACT_LEGAL_BASIS_LABELS,
  type ContactLegalBasis,
} from "@/lib/contacts";
import {
  buildImportPlan,
  EMPTY_COLUMN_MAP,
  EMPTY_VALUE_MAP,
  IMPORT_FIELDS,
  IMPORT_FIELD_HINTS,
  IMPORT_FIELD_LABELS,
  IMPORT_MAX_CONTACTS,
  suggestBundesland,
  suggestColumnMap,
  tallyColumn,
  tallyTagColumn,
  type ColumnMap,
  type ImportField,
  type ImportPlan,
  type ImportSheet,
  type ValueMap,
  type ValueTarget,
} from "@/lib/contacts-import";
import { IMPORT_ACCEPT, readImportFile } from "@/lib/contacts-import-file";
import {
  listImportMappings,
  listImportRuns,
  runContactsImport,
  saveImportMapping,
} from "@/lib/contacts-import.functions";
import { listContactTagOptions } from "@/lib/contacts.functions";
import { supabase } from "@/integrations/supabase/client";
import type { ImportReport } from "@/lib/contacts-import.server";


export const Route = createFileRoute("/_admin/admin/contacts-import")({
  component: ContactsImportPage,
  head: () => ({ meta: [{ title: "Kontakte einlesen – Interner Bereich" }] }),
});

const selectClass = adminInputClass + " pr-8";

function ContactsImportPage() {
  const { isAdmin, isEditor } = useAdminSession();
  const mayImport = isAdmin || isEditor;
  const queryClient = useQueryClient();

  const sourcesFn = useServerFn(listContactSources);
  const tagsFn = useServerFn(listContactTagOptions);
  const mappingsFn = useServerFn(listImportMappings);
  const runsFn = useServerFn(listImportRuns);
  const saveMappingFn = useServerFn(saveImportMapping);
  const importFn = useServerFn(runContactsImport);

  const sourcesQuery = useQuery({
    queryKey: ["admin", "contact-sources"],
    queryFn: () => sourcesFn(),
    staleTime: 60_000,
    enabled: mayImport,
  });
  const tagsQuery = useQuery({
    queryKey: ["admin", "contact-tag-options"],
    queryFn: () => tagsFn(),
    staleTime: 60_000,
    enabled: mayImport,
  });
  const mappingsQuery = useQuery({
    queryKey: ["admin", "contact-import-mappings"],
    queryFn: () => mappingsFn(),
    staleTime: 60_000,
    enabled: mayImport,
  });
  const runsQuery = useQuery({
    queryKey: ["admin", "contact-import-runs"],
    queryFn: () => runsFn(),
    staleTime: 30_000,
    enabled: mayImport,
  });

  const sources = useMemo(
    () => selectableSources(sourcesQuery.data ?? []),
    [sourcesQuery.data],
  );
  const tagOptions = useMemo(() => tagsQuery.data ?? [], [tagsQuery.data]);

  const [filename, setFilename] = useState("");
  const [sheet, setSheet] = useState<ImportSheet | null>(null);
  const [columnMap, setColumnMap] = useState<ColumnMap>(EMPTY_COLUMN_MAP);
  const [valueMap, setValueMap] = useState<ValueMap>(EMPTY_VALUE_MAP);
  const [legalBasis, setLegalBasis] = useState<ContactLegalBasis | "">("");
  const [proof, setProof] = useState("");
  const [reading, setReading] = useState(false);
  const [mappingName, setMappingName] = useState("");
  const [report, setReport] = useState<ImportReport | null>(null);
  const reportRef = useRef<HTMLDivElement | null>(null);

  const plan = useMemo(() => {
    if (!sheet || !columnMap.email) return null;
    return buildImportPlan({
      rows: sheet.rows,
      columnMap,
      valueMap,
    });
  }, [sheet, columnMap, valueMap]);

  const sourceValues = useMemo(
    () => tallyColumn(sheet?.rows ?? [], columnMap.source),
    [sheet, columnMap.source],
  );
  const tagValues = useMemo(
    () => tallyTagColumn(sheet?.rows ?? [], columnMap.tag),
    [sheet, columnMap.tag],
  );
  const landValues = useMemo(
    () => tallyColumn(sheet?.rows ?? [], columnMap.bundesland),
    [sheet, columnMap.bundesland],
  );

  /** Platzhalter „#key" tragen noch keine Kennung: Namen aus dem Vorhaben lesen. */
  function nameOfSource(ref: string): string {
    if (ref.startsWith("#")) {
      const hit = plan?.newSources.find((s) => `#${s.key}` === ref);
      return hit ? `${hit.name} (neu)` : "?";
    }
    return sources.find((s) => s.id === ref)?.name ?? "?";
  }

  function nameOfTag(ref: string): string {
    if (ref.startsWith("#")) {
      const hit = plan?.newTags.find((t) => `#${t.key}` === ref);
      return hit ? `${hit.name} (neu)` : "?";
    }
    return tagOptions.find((t) => t.id === ref)?.label ?? "?";
  }




  async function onFiles(files: FileList) {
    const file = files[0];
    if (!file) return;
    setReading(true);
    try {
      const read = await readImportFile(file);
      if (read.headers.length === 0 || read.rows.length === 0) {
        toast.error("Die Datei enthält keine lesbaren Zeilen.");
        return;
      }
      setSheet(read);
      setFilename(file.name);
      setColumnMap(suggestColumnMap(read.headers));
      // Bundesländer lassen sich anhand der Bezeichnung vorschlagen; Herkünfte
      // nicht, die entscheidet ein Mensch.
      const suggestion: ValueMap = { source: {}, bundesland: {}, tag: {} };
      const landColumn = suggestColumnMap(read.headers).bundesland;
      for (const { value } of tallyColumn(read.rows, landColumn)) {
        const hit = suggestBundesland(value);
        if (hit) suggestion.bundesland[value] = hit;
      }
      setValueMap(suggestion);
      setReport(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Die Datei ließ sich nicht lesen.");
    } finally {
      setReading(false);
    }
  }

  function applyMapping(id: string) {
    const row = (mappingsQuery.data ?? []).find((m) => m.id === id);
    if (!row) return;
    const next: ColumnMap = { ...EMPTY_COLUMN_MAP };
    for (const field of IMPORT_FIELDS) {
      const column = row.columnMap[field] ?? null;
      next[field] = column && sheet?.headers.includes(column) ? column : null;
    }
    setColumnMap(next);
    setValueMap({
      source: { ...row.valueMap.source },
      bundesland: { ...row.valueMap.bundesland },
      tag: { ...row.valueMap.tag },
    });

    toast.success(`Zuordnung „${row.name}" übernommen.`);
  }

  const saveMapping = useMutation({
    mutationFn: () => saveMappingFn({ data: { name: mappingName, columnMap, valueMap } }),
    onSuccess: () => {
      setMappingName("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "contact-import-mappings"] });
      toast.success("Zuordnung gespeichert.");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Zuordnung nicht gespeichert."),
  });

  const runImport = useMutation({
    mutationFn: async () => {
      if (!plan || !legalBasis) throw new Error("Der Vorgang ist noch nicht vollständig.");
      return importFn({
        data: {
          filename,
          legalBasis,
          legalBasisWordingVersion: proof.trim(),
          isTest: false,
          rowsRead: plan.stats.rowsRead,
          newSources: plan.newSources.map((s) => ({ key: s.key, name: s.name })),
          newTags: plan.newTags.map((t) => ({ key: t.key, name: t.name })),
          contacts: plan.contacts.map((c) => ({
            email: c.email,
            firstName: c.firstName,
            lastName: c.lastName,
            organization: c.organization,
            bundesland: c.bundesland,
            origins: c.origins,
            tags: c.tags,
          })),
          skipped: plan.skipped,
          conflicts: plan.conflicts,
          notes: buildNotes(plan.stats),
        },
      });
    },
    onSuccess: (result) => {
      setReport(result.report);
      void queryClient.invalidateQueries({ queryKey: ["admin", "contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "contact-import-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "contact-tag-options"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "contact-sources"] });

      toast.success("Der Bestand ist übernommen.");
      window.setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Der Import ist fehlgeschlagen."),
  });

  const blockers: string[] = [];
  if (!sheet) blockers.push("Es ist noch keine Datei gelesen.");
  if (!columnMap.email) blockers.push("Die Spalte mit der E-Mail-Adresse ist nicht zugeordnet.");
  if (!legalBasis) blockers.push("Die Rechtsgrundlage ist nicht gewählt.");
  if (proof.trim() === "") blockers.push("Der Verweis auf den Nachweis fehlt.");
  if (plan && plan.contacts.length === 0) blockers.push("Es bleibt keine übernehmbare Zeile übrig.");
  if (plan && plan.contacts.length > IMPORT_MAX_CONTACTS) {
    blockers.push(`Mehr als ${IMPORT_MAX_CONTACTS} Kontakte – bitte die Datei aufteilen.`);
  }

  if (!mayImport) {
    return (
      <div className="space-y-6">
        <AdminPageHeader title="Kontakte einlesen" />
        <AdminCard>
          <p className="text-sm text-muted-foreground">
            Das Einlesen von Kontakten ist Administration und Redaktion vorbehalten, weil
            dabei hunderte personenbezogene Datensätze entstehen.
          </p>
        </AdminCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Kontakte einlesen"
        description="Bestände aus anderen Werkzeugen übernehmen: Datei lesen, Spalten zuordnen, Werte übersetzen, übernehmen. Die Datei bleibt auf Ihrem Gerät und wird nicht hochgeladen. Mehrfach vorkommende Adressen werden zu einem Kontakt zusammengeführt."
      />

      {/* Schritt 1 */}
      <AdminCard title="1. Datei und Rechtsgrundlage">
        <div className="space-y-4">
          <AdminUploadButton
            label="Datei auswählen"
            accept={IMPORT_ACCEPT}
            uploading={reading}
            currentName={filename || null}
            hint="CSV mit Semikolon oder Komma sowie Excel (.xlsx, .xls). Die erste Zeile gilt als Kopfzeile."
            onFiles={(files) => void onFiles(files)}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <AdminField
              label="Rechtsgrundlage für alle Zeilen"
              required
              hint="Die Datei enthält keine Angabe zur Einwilligung, deshalb wird sie hier gemeinsam gesetzt."
            >
              {(props) => (
                <select
                  {...props}
                  className={selectClass}
                  value={legalBasis}
                  onChange={(e) => setLegalBasis(e.target.value as ContactLegalBasis | "")}
                >
                  <option value="">Bitte wählen …</option>
                  {CONTACT_LEGAL_BASES.map((b) => (
                    <option key={b} value={b}>
                      {CONTACT_LEGAL_BASIS_LABELS[b]}
                    </option>
                  ))}
                </select>
              )}
            </AdminField>

            <AdminField
              label="Verweis auf den Nachweis"
              required
              hint="Woher stammt die Einwilligung? Zum Beispiel: Anmeldeformular Wahlkalender, Stand 2023."
            >
              {(props) => (
                <input
                  {...props}
                  className={adminInputClass}
                  value={proof}
                  maxLength={300}
                  onChange={(e) => setProof(e.target.value)}
                />
              )}
            </AdminField>
          </div>
        </div>
      </AdminCard>

      {/* Schritt 2 */}
      {sheet ? (
        <AdminCard
          title="2. Spalten zuordnen"
          description={`${sheet.headers.length} Spalten, ${sheet.rows.length} Zeilen gelesen. Nur die E-Mail-Adresse ist Pflicht.`}
        >
          <div className="space-y-4">
            {(mappingsQuery.data ?? []).length > 0 ? (
              <AdminField label="Gespeicherte Zuordnung übernehmen">
                {(props) => (
                  <select
                    {...props}
                    className={selectClass}
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) applyMapping(e.target.value);
                      e.target.value = "";
                    }}
                  >
                    <option value="">Bitte wählen …</option>
                    {(mappingsQuery.data ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                )}
              </AdminField>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {IMPORT_FIELDS.map((field) => (
                <AdminField
                  key={field}
                  label={IMPORT_FIELD_LABELS[field]}
                  required={field === "email"}
                  hint={IMPORT_FIELD_HINTS[field] || undefined}
                >
                  {(props) => (
                    <select
                      {...props}
                      className={selectClass}
                      value={columnMap[field] ?? ""}
                      onChange={(e) =>
                        setColumnMap((prev) => ({
                          ...prev,
                          [field]: e.target.value === "" ? null : e.target.value,
                        }))
                      }
                    >
                      <option value="">— nicht zugeordnet —</option>
                      {sheet.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  )}
                </AdminField>
              ))}
            </div>

            <div className="flex flex-wrap items-end gap-2 border-t border-border pt-4">
              <div className="w-full max-w-xs">
                <AdminField label="Zuordnung speichern als">
                  {(props) => (
                    <input
                      {...props}
                      className={adminInputClass}
                      value={mappingName}
                      maxLength={120}
                      placeholder="z. B. Mailwerkzeug-Export"
                      onChange={(e) => setMappingName(e.target.value)}
                    />
                  )}
                </AdminField>
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={mappingName.trim() === "" || saveMapping.isPending}
                onClick={() => saveMapping.mutate()}
              >
                Speichern
              </Button>
            </div>
          </div>
        </AdminCard>
      ) : null}

      {/* Schritt 3 */}
      {sheet && plan ? (
        <>
          {columnMap.source ? (
            <AdminCard
              title="3a. Werte der Spalte Herkunft zuordnen"
              description="Jeder Wert wird einer geführten Herkunft zugeordnet oder ausdrücklich als neue Herkunft angelegt. Weglassen ist die Voreinstellung: die Zeile wird dann ohne Herkunft übernommen, sie scheitert nicht."
            >
              <TargetMapTable
                values={sourceValues}
                options={sources.map((s) => ({ value: s.id, label: s.name }))}
                selected={valueMap.source}
                newLabel="neue Herkunft anlegen"
                maxLength={120}
                onChange={(value, target) =>
                  setValueMap((prev) => ({ ...prev, source: applyTarget(prev.source, value, target) }))
                }
              />
            </AdminCard>
          ) : null}

          {columnMap.tag ? (
            <AdminCard
              title="3b. Werte der Spalte Schlagwort zuordnen"
              description="Mehrere Schlagworte je Zelle werden an Komma, Semikolon, Schrägstrich und senkrechtem Strich getrennt. Auch hier entsteht nichts von selbst: neue Schlagworte müssen ausdrücklich angelegt werden."
            >
              <TargetMapTable
                values={tagValues}
                options={tagOptions.map((t) => ({ value: t.id, label: t.label }))}
                selected={valueMap.tag}
                newLabel="neues Schlagwort anlegen"
                maxLength={60}
                onChange={(value, target) =>
                  setValueMap((prev) => ({ ...prev, tag: applyTarget(prev.tag, value, target) }))
                }
              />
            </AdminCard>
          ) : null}

          {columnMap.bundesland ? (
            <AdminCard
              title="3c. Werte der Spalte Bundesland zuordnen"
              description="Bekannte Bezeichnungen sind vorbelegt. Nicht zugeordnete Werte bleiben leer."
            >
              <ValueMapTable
                values={landValues}
                options={BUNDESLAENDER.map((b) => ({ value: b, label: BUNDESLAND_LABELS[b] }))}
                selected={valueMap.bundesland}
                onChange={(value, target) =>
                  setValueMap((prev) => ({
                    ...prev,
                    bundesland: applyValue(prev.bundesland, value, target),
                  }))
                }
              />
            </AdminCard>
          ) : null}

          <AdminCard title="3d. Vorschau und Prüfungen">
            <div className="space-y-5">
              <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <Figure label="Gelesene Zeilen" value={plan.stats.rowsRead} />
                <Figure label="Kontakte danach" value={plan.stats.contactsTotal} />
                <Figure
                  label="Mit mehr als einer Herkunft"
                  value={plan.stats.contactsWithSeveralOrigins}
                />
                <Figure label="Mit Schlagworten" value={plan.stats.contactsWithTags} />
                <Figure label="Mit Bundesland" value={plan.stats.contactsWithBundesland} />
                <Figure label="Adressen in mehreren Zeilen" value={plan.stats.multiRowEmails} />
                <Figure label="Übersprungene Zeilen" value={plan.skipped.length} tone="warn" />
                <Figure
                  label="Unlesbare Datumsangaben"
                  value={plan.stats.unreadableDates}
                  tone={plan.stats.unreadableDates > 0 ? "warn" : "plain"}
                />
              </dl>

              <UnmappedWarning plan={plan} />

              {plan.newSources.length > 0 || plan.newTags.length > 0 ? (
                <div className="rounded-md border border-border bg-secondary/40 p-4 text-sm">
                  <p className="font-semibold">Neu angelegt wird</p>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    {plan.newSources.map((s) => (
                      <li key={`s-${s.key}`}>
                        Herkunft „{s.name}" ({s.rows} Zeilen)
                      </li>
                    ))}
                    {plan.newTags.map((t) => (
                      <li key={`t-${t.key}`}>
                        Schlagwort „{t.name}" ({t.rows} Zeilen)
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {plan.stats.sameValueRows > 0 ? (
                <div className="rounded-md border border-border bg-secondary/40 p-4 text-sm">
                  <p className="font-semibold">
                    Hinweis: Herkunft und Schlagwort entstehen aus demselben Wert
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    In {plan.stats.sameValueRows} Zeilen tragen Herkunft und Schlagwort denselben
                    Wert. Die Aussage steht dann zweimal am Kontakt. Betroffen:{" "}
                    {plan.stats.sameValues.map((v) => `„${v.value}" (${v.rows})`).join(", ")}
                  </p>
                </div>
              ) : null}

              {plan.conflicts.length > 0 ? (
                <ConflictList conflicts={plan.conflicts} />
              ) : null}



              {plan.skipped.length > 0 ? (
                <div className="rounded-md border border-border bg-secondary/40 p-4">
                  <p className="text-sm font-semibold">
                    Übersprungene Zeilen ({plan.skipped.length})
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {plan.skipped.slice(0, 20).map((s) => (
                      <li key={`${s.rowNumber}-${s.email}`}>
                        Zeile {s.rowNumber}: {s.reason}
                        {s.email ? ` („${s.email}")` : ""}
                      </li>
                    ))}
                    {plan.skipped.length > 20 ? (
                      <li>… und {plan.skipped.length - 20} weitere im Bericht.</li>
                    ) : null}
                  </ul>
                </div>
              ) : null}

              <AdminTableShell maxHeightClass="max-h-[42vh]">
                <table className="w-full min-w-[980px] text-sm">
                  <thead>
                    <tr className="text-left">
                      <th className="px-3 py-2 font-semibold">E-Mail</th>
                      <th className="px-3 py-2 font-semibold">Name</th>
                      <th className="px-3 py-2 font-semibold">Organisation</th>
                      <th className="px-3 py-2 font-semibold">Bundesland</th>
                      <th className="px-3 py-2 font-semibold">Herkünfte</th>
                      <th className="px-3 py-2 font-semibold">Schlagworte</th>
                      <th className="px-3 py-2 font-semibold">Zeilen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.contacts.slice(0, 25).map((c) => (
                      <tr key={c.email} className="border-t border-border align-top">
                        <td className="px-3 py-2">{c.email}</td>
                        <td className="px-3 py-2">{`${c.firstName} ${c.lastName}`.trim() || "—"}</td>
                        <td className="px-3 py-2">{c.organization ?? "—"}</td>
                        <td className="px-3 py-2">
                          {c.bundesland ? BUNDESLAND_LABELS[c.bundesland] : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {c.origins.length === 0
                            ? "—"
                            : c.origins
                                .map(
                                  (o) =>
                                    `${nameOfSource(o.sourceId)} (${formatDate(o.occurredAt)})`,
                                )
                                .join(", ")}
                        </td>
                        <td className="px-3 py-2">
                          {c.tags.length === 0 ? "—" : c.tags.map(nameOfTag).join(", ")}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {c.rowNumbers.join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </AdminTableShell>

            </div>
          </AdminCard>

          {/* Schritt 4 */}
          <AdminCard
            title="4. Übernehmen"
            description="Der Vorgang läuft in einem Zug. Bricht er ab, wird nichts übernommen."
          >
            <div className="space-y-4">
              {blockers.length > 0 ? (
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {blockers.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" aria-hidden />
                      {b}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  {plan.stats.contactsTotal} Kontakte aus {plan.stats.rowsRead} Zeilen sind
                  vorbereitet.
                </p>
              )}
              <Button
                type="button"
                disabled={blockers.length > 0 || runImport.isPending}
                onClick={() => runImport.mutate()}
              >
                {runImport.isPending ? "Wird übernommen …" : "Bestand übernehmen"}
              </Button>
            </div>
          </AdminCard>
        </>
      ) : null}

      {report ? (
        <div ref={reportRef}>
          <ReportCard title="Bericht zum Import" report={report} />
        </div>
      ) : null}

      <AdminCard
        title="Frühere Läufe"
        description="Jeder Bericht bleibt abrufbar und steht zusätzlich im Protokoll."
      >
        {runsQuery.isError ? (
          <AdminErrorNotice message="Die früheren Läufe ließen sich nicht laden." />
        ) : (runsQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Bislang wurde kein Bestand eingelesen.</p>
        ) : (
          <ul className="space-y-3">
            {(runsQuery.data ?? []).map((run) => (
              <li key={run.id} className="rounded-md border border-border p-4">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  <FileSpreadsheet className="h-4 w-4" aria-hidden />
                  {run.filename || "ohne Dateinamen"}
                  <span className="font-normal text-muted-foreground">
                    {new Date(run.createdAt).toLocaleString("de-DE")}
                    {run.state === "ok" ? "" : " – fehlgeschlagen, nichts übernommen"}
                  </span>
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {run.report.rowsRead ?? run.rowsRead} Zeilen gelesen,{" "}
                  {run.report.contactsCreated ?? 0} Kontakte angelegt,{" "}
                  {run.report.contactsMerged ?? 0} ergänzt, {run.report.skipped?.length ?? 0}{" "}
                  übersprungen, {run.report.conflicts?.length ?? 0} Widersprüche.
                </p>
              </li>
            ))}
          </ul>
        )}
      </AdminCard>
    </div>
  );
}

function applyValue(
  map: Record<string, string>,
  value: string,
  target: string,
): Record<string, string> {
  const next = { ...map };
  if (target === "") delete next[value];
  else next[value] = target;
  return next;
}

function applyTarget(
  map: Record<string, ValueTarget>,
  value: string,
  target: ValueTarget | null,
): Record<string, ValueTarget> {
  const next = { ...map };
  if (target === null) delete next[value];
  else next[value] = target;
  return next;
}

function buildNotes(stats: ImportPlan["stats"]): string[] {
  const notes: string[] = [];
  if (stats.unreadableDates > 0) {
    notes.push(
      `${stats.unreadableDates} Zeilen mit unlesbarer Datumsangabe – dort gilt der Importzeitpunkt.`,
    );
  }
  if (stats.unmappedSourceValues > 0) {
    notes.push(
      `${stats.unmappedSourceValues} Zeilen mit nicht zugeordneter Herkunft: ${stats.unmappedSource
        .map((v) => `„${v.value}"`)
        .join(", ")}.`,
    );
  }
  if (stats.unmappedTagValues > 0) {
    notes.push(
      `${stats.unmappedTagValues} Werte der Schlagwortspalte bleiben unzugeordnet: ${stats.unmappedTag
        .map((v) => `„${v.value}"`)
        .join(", ")}.`,
    );
  }
  if (stats.unmappedBundeslandValues > 0) {
    notes.push(`${stats.unmappedBundeslandValues} Zeilen mit nicht zugeordnetem Bundesland.`);
  }
  if (stats.missingSource > 0) {
    notes.push(`${stats.missingSource} Zeilen ohne Angabe in der Herkunftsspalte.`);
  }
  if (stats.missingTag > 0) {
    notes.push(`${stats.missingTag} Zeilen ohne Angabe in der Schlagwortspalte.`);
  }
  if (stats.sameValueRows > 0) {
    notes.push(
      `${stats.sameValueRows} Zeilen, in denen Herkunft und Schlagwort aus demselben Wert entstehen.`,
    );
  }
  return notes;
}

/**
 * Anmerkung 1 der Freigabe: Weglassen bleibt die Voreinstellung, aber es wird
 * deutlich gewarnt – mit Wert und betroffener Zeilenzahl, nicht nur als Zählung.
 */
function UnmappedWarning({ plan }: { plan: ImportPlan }) {
  const groups = [
    { label: "Herkunft", entries: plan.stats.unmappedSource, effect: "Zeilen ohne Herkunft" },
    { label: "Schlagwort", entries: plan.stats.unmappedTag, effect: "Werte ohne Schlagwort" },
    { label: "Bundesland", entries: plan.stats.unmappedBundesland, effect: "Zeilen ohne Bundesland" },
  ].filter((g) => g.entries.length > 0);
  if (groups.length === 0) return null;

  return (
    <div className="rounded-md border-2 border-brand-orange bg-brand-orange/10 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="h-4 w-4 shrink-0 text-brand-orange" aria-hidden />
        Achtung: nicht zugeordnete Werte gehen verloren
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Diese Werte werden weggelassen. Die Kontakte entstehen trotzdem, aber ohne die genannte
        Angabe. Ordnen Sie die Werte zu oder legen Sie sie ausdrücklich neu an, wenn Sie sie
        behalten möchten.
      </p>
      <ul className="mt-3 space-y-2 text-sm">
        {groups.map((group) => (
          <li key={group.label}>
            <strong className="font-semibold">{group.label}</strong> – {group.effect}:{" "}
            {group.entries.map((e) => `„${e.value}" (${e.rows})`).join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
}


function formatDate(iso: string | null): string {
  if (!iso) return "ohne Datum";
  return new Date(iso).toLocaleDateString("de-DE");
}

function Figure({
  label,
  value,
  tone = "plain",
}: {
  label: string;
  value: number;
  tone?: "plain" | "warn";
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={
          "font-display text-2xl font-extrabold " + (tone === "warn" && value > 0 ? "text-brand-orange" : "")
        }
      >
        {value}
      </dd>
    </div>
  );
}

/** Punkt 3 des Tickets: Widersprüche entscheidet ein Mensch, nicht die Technik. */
function ConflictList({
  conflicts,
}: {
  conflicts: { email: string; field: string; kept: string; other: string; rowNumbers: number[] }[];
}) {
  return (
    <div className="rounded-md border border-brand-orange/40 bg-brand-orange/5 p-4">
      <p className="text-sm font-semibold">Bitte nachsehen: widersprüchliche Angaben ({conflicts.length})</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Übernommen wird jeweils der Wert der ersten Zeile. Die Entscheidung sollte ein Mensch
        treffen – von Hand am Kontakt oder über die Sammelaktionen.
      </p>
      <ul className="mt-2 space-y-1 text-sm">
        {conflicts.map((c, index) => (
          <li key={`${c.email}-${c.field}-${index}`}>
            <strong className="font-semibold">{c.email}</strong> – {c.field}: übernommen „{c.kept}",
            abweichend „{c.other}" (Zeile {c.rowNumbers.join(", ")})
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReportCard({ title, report }: { title: string; report: ImportReport }) {
  return (
    <AdminCard title={title}>
      <div className="space-y-5">
        <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Figure label="Gelesene Zeilen" value={report.rowsRead} />
          <Figure label="Kontakte angelegt" value={report.contactsCreated} />
          <Figure label="Bestehende ergänzt" value={report.contactsMerged} />
          <Figure label="Kontakte gesamt" value={report.contactsTotal} />
          <Figure label="Mit mehr als einer Herkunft" value={report.contactsWithSeveralOrigins} />
          <Figure label="Mit Bundesland" value={report.contactsWithBundesland} />
          <Figure label="Herkünfte neu" value={report.originsCreated} />
          <Figure label="Herkünfte zusammengeführt" value={report.originsKept} />
          <Figure label="Schlagworte vergeben" value={report.tagLinksCreated ?? 0} />
          <Figure label="Schlagworte bereits vorhanden" value={report.tagLinksKept ?? 0} />
        </dl>

        {(report.sourcesCreated ?? []).length > 0 || (report.tagsCreated ?? []).length > 0 ? (
          <div>
            <p className="text-sm font-semibold">Ausdrücklich neu angelegt</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {(report.sourcesCreated ?? []).map((s) => (
                <li key={`ns-${s.name}`}>Herkunft: {s.name}</li>
              ))}
              {(report.tagsCreated ?? []).map((t) => (
                <li key={`nt-${t.name}`}>Schlagwort: {t.name}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {(report.perSource ?? []).length > 0 ? (
          <div>
            <p className="text-sm font-semibold">Zuordnungen je Herkunft</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {report.perSource.map((s) => (
                <li key={s.name}>
                  {s.name}: {s.count}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(report.perTag ?? []).length > 0 ? (
          <div>
            <p className="text-sm font-semibold">Zuordnungen je Schlagwort</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {report.perTag.map((t) => (
                <li key={t.name}>
                  {t.name}: {t.count}
                </li>
              ))}
            </ul>
          </div>
        ) : null}


        {(report.conflicts ?? []).length > 0 ? <ConflictList conflicts={report.conflicts} /> : null}

        {(report.skipped ?? []).length > 0 ? (
          <div className="rounded-md border border-border bg-secondary/40 p-4">
            <p className="text-sm font-semibold">Übersprungene Zeilen ({report.skipped.length})</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {report.skipped.map((s) => (
                <li key={`${s.rowNumber}-${s.email}`}>
                  Zeile {s.rowNumber}: {s.reason}
                  {s.email ? ` („${s.email}")` : ""}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(report.notes ?? []).length > 0 ? (
          <ul className="space-y-1 text-sm text-muted-foreground">
            {report.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </AdminCard>
  );
}

function ValueMapTable({
  values,
  options,
  selected,
  onChange,
}: {
  values: { value: string; count: number }[];
  options: { value: string; label: string }[];
  selected: Record<string, string>;
  onChange: (value: string, target: string) => void;
}) {
  if (values.length === 0) {
    return <p className="text-sm text-muted-foreground">In dieser Spalte steht kein Wert.</p>;
  }
  return (
    <AdminTableShell maxHeightClass="max-h-[40vh]">
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="text-left">
            <th className="px-3 py-2 font-semibold">Wert in der Datei</th>
            <th className="px-3 py-2 font-semibold">Zeilen</th>
            <th className="px-3 py-2 font-semibold">Zuordnung</th>
          </tr>
        </thead>
        <tbody>
          {values.map((v) => (
            <tr key={v.value} className="border-t border-border">
              <td className="px-3 py-2">{v.value}</td>
              <td className="px-3 py-2 text-muted-foreground">{v.count}</td>
              <td className="px-3 py-2">
                <select
                  className={selectClass + " max-w-xs"}
                  aria-label={`Zuordnung für ${v.value}`}
                  value={selected[v.value] ?? ""}
                  onChange={(e) => onChange(v.value, e.target.value)}
                >
                  <option value="">— nicht zuordnen —</option>
                  {options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </AdminTableShell>
  );
}

/**
 * Zuordnung mit ausdrücklicher Neuanlage: „Weglassen" ist vorbelegt, eine
 * Neuanlage entsteht erst, wenn sie hier gewählt und benannt wird.
 */
function TargetMapTable({
  values,
  options,
  selected,
  newLabel,
  maxLength,
  onChange,
}: {
  values: { value: string; count: number }[];
  options: { value: string; label: string }[];
  selected: Record<string, ValueTarget>;
  newLabel: string;
  maxLength: number;
  onChange: (value: string, target: ValueTarget | null) => void;
}) {
  if (values.length === 0) {
    return <p className="text-sm text-muted-foreground">In dieser Spalte steht kein Wert.</p>;
  }
  return (
    <AdminTableShell maxHeightClass="max-h-[40vh]">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="text-left">
            <th className="px-3 py-2 font-semibold">Wert in der Datei</th>
            <th className="px-3 py-2 font-semibold">Zeilen</th>
            <th className="px-3 py-2 font-semibold">Zuordnung</th>
            <th className="px-3 py-2 font-semibold">Bezeichnung der Neuanlage</th>
          </tr>
        </thead>
        <tbody>
          {values.map((v) => {
            const target = selected[v.value];
            const isNew = target?.kind === "new";
            return (
              <tr key={v.value} className="border-t border-border align-middle">
                <td className="px-3 py-2">{v.value}</td>
                <td className="px-3 py-2 text-muted-foreground">{v.count}</td>
                <td className="px-3 py-2">
                  <select
                    className={selectClass + " max-w-xs"}
                    aria-label={`Zuordnung für ${v.value}`}
                    value={isNew ? "__new__" : (target?.id ?? "")}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === "") onChange(v.value, null);
                      else if (next === "__new__")
                        onChange(v.value, { kind: "new", name: v.value.slice(0, maxLength) });
                      else onChange(v.value, { kind: "existing", id: next });
                    }}
                  >
                    <option value="">— weglassen —</option>
                    {options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                    <option value="__new__">+ {newLabel}</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  {isNew ? (
                    <input
                      className={adminInputClass + " max-w-xs"}
                      aria-label={`Bezeichnung für ${v.value}`}
                      value={target.name}
                      maxLength={maxLength}
                      onChange={(e) =>
                        onChange(v.value, { kind: "new", name: e.target.value })
                      }
                    />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </AdminTableShell>
  );
}

